import Popover from 'trunks/vue/admin/components/libs/popover.js'
import { newAlertify } from 'trunks/vue/admin/components/alert.js'
import NamespacesContainer from './namespaces_container.js'
import SuperiorsPanel from './superiors_panel.js'
import TopOrganizationsPanel from './top_organizations_panel.js'

export default Vue.extend({
  components: {
    'namespaces-container': NamespacesContainer,
    'superiors-panel': SuperiorsPanel,
    'top-organizations-panel': TopOrganizationsPanel,
    Popover
  },

  props: {
    initialNamespace: {
      type: Object,
      required: true
    },
    features: {
      type: Array,
      default() {
        return []
      }
    }
  },

  data() {
    return {
      namespace: this.initialNamespace,
      searchKeyword: '',
      searchResults: [],
      searching: false,
      _searchTimer: null,
      refreshingToken: false
    }
  },

  computed: {
    enabledFeatureLabels() {
      const labelsMap = {}
      this.features.forEach((item) => {
        labelsMap[item.id] = item.name
      })
      return (this.namespace.features || []).map((key) => labelsMap[key] || key)
    },

    showSearchResults() {
      return this.searchKeyword.length > 0
    }
  },

  watch: {
    searchKeyword(value) {
      if (this._searchTimer) clearTimeout(this._searchTimer)
      if (!value) {
        this.searchResults = []
        return
      }

      this._searchTimer = setTimeout(() => {
        this._queryUsers(value)
      }, 300)
    }
  },

  template: `
    <namespaces-container :title="namespace.name">
      <template slot="actions">
        <a :href="namespace.urls.edit" class="btn btn-default btn-small outlined mr-3">编辑</a>
        <Popover position="bottom right"
                 customClass="confirm table-operation-confirm-popover delete-confirm-popover"
                 ref="deleteNamespacePopoverRef">
          <template slot="trigger">
            <button class="btn btn-danger btn-small">删除空间</button>
          </template>
          <template slot="content">
            <div class="popover-confirm-content">
              <div class="popover-confirm-title">确定删除该空间？</div>
              <div class="popover-confirm-message text-danger">删除后空间内的所有数据都将无法恢复。</div>
              <div class="popover-confirm-footer">
                <span class="btn btn-small btn-default outlined" @click.stop="closeDeletePopover">取消</span>
                <span class="btn btn-small btn-danger" @click.stop="destroyNamespace">删除</span>
              </div>
            </div>
          </template>
        </Popover>
      </template>

      <template slot="content">
        <div class="namespace-detail">
          <div class="login-search-field">
            <div class="input-control left-addon">
              <input type="text"
                     v-model.trim="searchKeyword"
                     class="controlled-input floated"
                     placeholder="以空间成员登录（输入姓名或手机号）" />
              <div class="addon"><i class="slp-search"></i></div>
              <span class="addon-right" v-if="searchKeyword" @click="clearSearch">
                <i class="slp-times"></i>
              </span>
            </div>

            <div class="search-results" v-if="showSearchResults">
              <div v-if="searching" class="text-muted">搜索中...</div>
              <div v-else-if="searchResults.length === 0" class="text-muted">未找到匹配的成员</div>
              <table v-else class="table table-noborder table-striped">
                <thead>
                  <tr>
                    <th>姓名</th>
                    <th>识别码</th>
                    <th>手机号</th>
                    <th class="operation-col">操作</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="user in searchResults" :key="user.id">
                    <td>{{ user.name }}</td>
                    <td>{{ user.identifier }}</td>
                    <td>{{ user.phone }}</td>
                    <td class="operation-col">
                      <a :href="'/admin/login_as_root/' + user.id"
                         data-method="post"
                         rel="nofollow"
                         data-turbolinks="false"
                         class="btn-text primary-text">登录</a>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="panel-card">
            <div class="panel-card-head">
              <span class="panel-card-title">基本信息</span>
            </div>
            <div class="panel-card-body">
              <table class="table table-noborder break-word-table info-table">
                <tbody>
                  <tr>
                    <th>名字</th>
                    <td>{{ namespace.name }}</td>
                  </tr>
                  <tr>
                    <th>空间后缀名</th>
                    <td>{{ namespace.site_name || '—' }}</td>
                  </tr>
                  <tr>
                    <th>APPID</th>
                    <td>{{ namespace.appid || '—' }}</td>
                  </tr>
                  <tr>
                    <th>APPSECRET</th>
                    <td>{{ namespace.appsecret || '—' }}</td>
                  </tr>
                  <tr>
                    <th>Token</th>
                    <td>
                      <span class="mr-3">{{ namespace.token }}</span>
                      <button class="btn btn-danger btn-xs"
                              :disabled="refreshingToken"
                              @click="refreshToken">{{ refreshingToken ? '更新中...' : '更新 Token' }}</button>
                    </td>
                  </tr>
                  <tr>
                    <th>Private Token</th>
                    <td>{{ namespace.private_token }}</td>
                  </tr>
                  <tr>
                    <th>URL</th>
                    <td>
                      <a :href="namespace.wechat_url" target="_blank">{{ namespace.wechat_url }}</a>
                    </td>
                  </tr>
                  <tr>
                    <th>转发微信请求地址</th>
                    <td>{{ namespace.remote_responder_uri || '—' }}</td>
                  </tr>
                  <tr>
                    <th>转发微信请求 Token</th>
                    <td>{{ namespace.remote_responder_token || '—' }}</td>
                  </tr>
                  <tr>
                    <th>短信中添加短链接</th>
                    <td>{{ namespace.short_url_in_sms ? '是' : '否' }}</td>
                  </tr>
                  <tr>
                    <th>使用自定义站点图标</th>
                    <td>{{ namespace.enable_custom_favicon ? '是' : '否' }}</td>
                  </tr>
                  <tr>
                    <th>用户数</th>
                    <td>{{ namespace.users_count }}（{{ namespace.binded_users_count }} 人绑定）</td>
                  </tr>
                  <tr>
                    <th>组织数</th>
                    <td>{{ namespace.organizations_count }}</td>
                  </tr>
                  <tr>
                    <th>开启的功能</th>
                    <td>{{ enabledFeatureLabels.length > 0 ? enabledFeatureLabels.join('，') : '—' }}</td>
                  </tr>
                  <tr>
                    <th>JWT Secret</th>
                    <td>{{ namespace.jwt_data.secret || '—' }}</td>
                  </tr>
                  <tr>
                    <th>JWT CID</th>
                    <td>{{ namespace.jwt_data.cid || '—' }}</td>
                  </tr>
                  <tr>
                    <th>JWT Additional Data</th>
                    <td>{{ namespace.jwt_data.additional_data || '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <superiors-panel :namespace="namespace" />

          <top-organizations-panel :namespace="namespace" />
        </div>
      </template>
    </namespaces-container>
  `,

  methods: {
    refreshToken() {
      if (this.refreshingToken) return
      if (!window.confirm('确认更新 Token？')) return

      this.refreshingToken = true
      Skylark.Libs.Ajax.patch(this.namespace.urls.refresh_token, {
        contentType: 'application/json; charset=utf-8',
        responseType: 'json',
        success: (data) => {
          this.namespace = data
          newAlertify('Token 更新成功', { type: 'success' })
        },
        error: () => {
          newAlertify('Token 更新失败', { type: 'error' })
        },
        complete: () => {
          this.refreshingToken = false
        }
      })
    },

    destroyNamespace() {
      Skylark.Libs.Ajax.delete(this.namespace.urls.destroy, {
        contentType: 'application/json; charset=utf-8',
        responseType: 'json',
        success: () => {
          newAlertify('空间删除成功', { type: 'success' })
          Turbolinks.visit('/admin/namespaces')
        },
        error: () => {
          newAlertify('空间删除失败', { type: 'error' })
        },
        complete: () => {
          this.closeDeletePopover()
        }
      })
    },

    closeDeletePopover() {
      if (this.$refs.deleteNamespacePopoverRef) {
        this.$refs.deleteNamespacePopoverRef.close()
      }
    },

    clearSearch() {
      this.searchKeyword = ''
      this.searchResults = []
    },

    _queryUsers(keyword) {
      this.searching = true
      Skylark.Libs.Ajax.get(this.namespace.urls.users_query, {
        responseType: 'json',
        data: { name: keyword },
        success: (data) => {
          this.searchResults = data || []
        },
        error: () => {
          this.searchResults = []
        },
        complete: () => {
          this.searching = false
        }
      })
    }
  }
})
